import { useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getRateHistory, type RateCurrency } from '../../shared/api/exchangeRates.api'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { Spinner } from '../../shared/ui/Spinner'
import { RateHistoryTable } from './RateHistoryTable'

/** Сколько записей архива подгружаем за раз. */
const ARCHIVE_PAGE = 50

/**
 * Архив истории курсов: всё, что старше «последних» записей. Свёрнут, пока его
 * не откроют; записи из базы не удаляются — подгружаются порциями по 50.
 */
export function RateHistoryArchive({
  currency,
  skip,
}: {
  currency: RateCurrency | null
  /** Сколько самых новых записей уже показано выше. */
  skip: number
}) {
  const [open, setOpen] = useState(false)

  const archive = useInfiniteQuery({
    queryKey: ['exchange-rate-history-archive', currency, skip],
    queryFn: ({ pageParam }) =>
      getRateHistory({
        currency: currency ?? undefined,
        limit: ARCHIVE_PAGE,
        offset: pageParam,
      }),
    initialPageParam: skip,
    getNextPageParam: (last, pages) =>
      last.hasMore ? skip + pages.reduce((n, p) => n + p.items.length, 0) : undefined,
    enabled: open,
  })

  const rows = archive.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-xl"
      >
        <span>Архив — более ранние изменения</span>
        <span className="text-gray-400">{open ? 'Свернуть' : 'Показать'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {archive.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner className="h-5 w-5" />
            </div>
          ) : archive.isError ? (
            <ErrorMessage error={archive.error} />
          ) : (
            <>
              <RateHistoryTable rows={rows} label="Архив изменений курса" />
              {archive.hasNextPage && (
                <button
                  type="button"
                  onClick={() => void archive.fetchNextPage()}
                  disabled={archive.isFetchingNextPage}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  {archive.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
